import { db, app, auth } from "@/lib/firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  doc, getDoc, updateDoc, collection, query, where, getDocs, limit,
  addDoc, serverTimestamp, orderBy,
} from "firebase/firestore";
import type {
  ApiResponse, KpiData, FunnelData, GeoData, PLGRow, Mentor,
  PLGDetail, Learner, MentorWorkload, AtRiskPLG, OperationalAlert,
  ActivationTrendData, TrackPipeline, DataQualityStats, Meeting, ConversationStarter,
  Track, LearnerRow, MentorActivityItem, ImportRow, ImportResult,
  FeedbackItem, FeedbackType, FeedbackStatus, ProgressReportPayload,
  ScheduledPost, ScheduledPostStatus, LearningAnalyticsData, PipelineVennsData, CohortAnalyticsData,
  TeamMember, UserRoleValue, CalendarConnection, MentorAvailability, WeeklyAvailabilitySlot,
  BookingSlot, Booking, CalendarListEntry, AudienceSegment, MessagingRequest,
} from "./types";

const SUPABASE_API_BASE = "https://iwqxzvcjujpgelbaovnc.supabase.co/functions/v1/api";
const SUPABASE_FUNCTIONS_BASE = "https://iwqxzvcjujpgelbaovnc.supabase.co/functions/v1";

const functions = getFunctions(app);

// fallbackName used when plg/name fields absent from Firestore doc
function mapRawToDetail(rawData: Record<string, unknown>, fallbackName: string): PLGDetail {
  const spaceData = (rawData.space as Record<string, unknown>) || rawData;
  const space: PLGRow = {
    id: 0,
    spaceId:       (spaceData.spaceId as number)      ?? 0,
    name:          (spaceData.plg as string)           ?? (spaceData.name as string) ?? fallbackName,
    track:         ((spaceData.track as string)        ?? "KCNA") as Track,
    mentor:        (spaceData.mentor as string)        ?? "Unknown",
    mentorEmail:   (spaceData.mentorEmail as string)   ?? "",
    learnerCount:  (spaceData.learnerCount as number)  ?? 0,
    activationPct: (spaceData.activationPct as number) ?? 0,
    hasModerator:  (spaceData.hasModerator as boolean) ?? false,
    mentorActive:  (spaceData.mentorActive as boolean) ?? false,
    riskScore:     (spaceData.riskScore as number)     ?? 0,
    tags:          Array.isArray(spaceData.tags) ? spaceData.tags as string[] : [],
  };
  return {
    space,
    learners:       Array.isArray(rawData.learners)       ? rawData.learners as LearnerRow[]           : [],
    mentorActivity: Array.isArray(rawData.mentorActivity) ? rawData.mentorActivity as MentorActivityItem[] : [],
  };
}

export const apiClient = {
  getKpis: async (_track?: string): Promise<ApiResponse<KpiData>> => {
    try {
      const s = await getDoc(doc(db, "aggregates", "kpi"));
      if (s.exists()) {
        const d = s.data() as Record<string, unknown>;
        return {
          success: true,
          data: {
            totalEnrolled: { value: (d.totalEnrolled as number) ?? 0, prevMonth: (d.totalEnrolledPrev as number) ?? 0, target: 10000 },
            activationRate: { value: (d.activationRate as number) ?? 0, prevMonth: (d.activationRatePrev as number) ?? 0, target: 75 },
            examPassRate: { value: (d.examPassRate as number) ?? 0, prevMonth: (d.examPassRatePrev as number) ?? 0, target: 55 },
            avgTimeToCert: { value: (d.avgTimeToCert as number) ?? 0, prevMonth: (d.avgTimeToCertPrev as number) ?? 0, unit: "weeks" },
          },
        };
      }
      return { success: false, error: "KPI data not found." };
    } catch (e: unknown) {
      return { success: false, error: e instanceof Error ? e.message : "Failed to fetch KPI data." };
    }
  },

  getFunnel: async (_track?: string): Promise<ApiResponse<FunnelData[]>> => {
    try {
      const s = await getDoc(doc(db, "aggregates", "funnel"));
      if (s.exists()) return { success: true, data: s.data().stages as FunnelData[] };
      return { success: false, error: "Funnel data not found." };
    } catch (e: unknown) {
      return { success: false, error: e instanceof Error ? e.message : "Failed to fetch Funnel data." };
    }
  },

  getLearningAnalytics: async (): Promise<ApiResponse<LearningAnalyticsData>> => {
    try {
      const s = await getDoc(doc(db, "aggregates", "learningAnalytics"));
      if (s.exists()) return { success: true, data: s.data() as LearningAnalyticsData };
      return { success: false, error: "Learning analytics data not found." };
    } catch (e: unknown) {
      return { success: false, error: e instanceof Error ? e.message : "Failed to fetch learning analytics." };
    }
  },

  getCohortAnalytics: async (): Promise<ApiResponse<CohortAnalyticsData>> => {
    try {
      const s = await getDoc(doc(db, "aggregates", "cohortAnalytics"));
      if (s.exists()) return { success: true, data: s.data() as CohortAnalyticsData };
      return { success: false, error: "Cohort analytics data not found." };
    } catch (e: unknown) {
      return { success: false, error: e instanceof Error ? e.message : "Failed to fetch cohort analytics." };
    }
  },

  getPipelineVenns: async (): Promise<ApiResponse<PipelineVennsData>> => {
    try {
      const s = await getDoc(doc(db, "aggregates", "pipelineVenns"));
      if (s.exists()) return { success: true, data: s.data() as PipelineVennsData };
      return { success: false, error: "Pipeline venn data not found." };
    } catch (e: unknown) {
      return { success: false, error: e instanceof Error ? e.message : "Failed to fetch pipeline venns." };
    }
  },

  getGeoData: async (_track?: string): Promise<ApiResponse<GeoData[]>> => {
    try {
      const s = await getDoc(doc(db, "aggregates", "geo"));
      if (s.exists()) return { success: true, data: s.data().geoData as GeoData[] };
      return { success: false, error: "Geo data not found." };
    } catch (e: unknown) {
      return { success: false, error: e instanceof Error ? e.message : "Failed to fetch Geo data." };
    }
  },
  
  getPLGs: async (track?: string): Promise<ApiResponse<PLGRow[]>> => {
    try {
      const col = collection(db, "plgDetails");
      const q = (track && track !== "all")
        ? query(col, where("track", "==", track.toUpperCase()))
        : col;
      const snap = await getDocs(q);
      const data: PLGRow[] = snap.docs.map((d, i) => {
        const raw = d.data() as Record<string, unknown>;
        return {
          id: i + 1,
          spaceId: (raw.spaceId as number) ?? 0,
          name: (raw.plg as string) ?? d.id.replace(/_/g, " "),
          track: (raw.track as Track) ?? "KCNA",
          mentor: (raw.mentor as string) ?? "Unknown",
          mentorEmail: (raw.mentorEmail as string) ?? "",
          learnerCount: (raw.learnerCount as number) ?? 0,
          activationPct: (raw.activationPct as number) ?? 0,
          hasModerator: (raw.hasModerator as boolean) ?? false,
          mentorActive: (raw.mentorActive as boolean) ?? false,
          riskScore: (raw.riskScore as number) ?? 0,
          mentors: Array.isArray(raw.mentors) ? raw.mentors as { name: string; email: string }[] : undefined,
        } satisfies PLGRow;
      });
      return { success: true, data };
    } catch (e: unknown) {
      return { success: false, error: e instanceof Error ? e.message : "Failed to fetch PLGs." };
    }
  },

  getMentors: async (): Promise<ApiResponse<Mentor[]>> => {
    try {
      const plgResponse = await apiClient.getPLGs();
      if (!plgResponse.success || !plgResponse.data) {
        return { success: false, error: "Could not fetch PLGs to derive mentor list." };
      }
      const data: Mentor[] = plgResponse.data.map((p): Mentor => ({
        name: p.mentor,
        email: p.mentorEmail,
        plg: p.name,
        track: p.track,
      }));

      return { success: true, data };
    } catch {
      return { success: false, error: "Failed to derive mentor list." };
    }
  },

  getPLGDetail: async (spaceId: number): Promise<ApiResponse<PLGDetail>> => {
    try {
      const q = query(collection(db, "plgDetails"), where("spaceId", "==", spaceId), limit(1));
      const snap = await getDocs(q);
      if (snap.empty) return { success: false, error: "PLG detail not found in database." };
      const rawData = snap.docs[0].data() as Record<string, unknown>;
      return { success: true, data: mapRawToDetail(rawData, String(spaceId)) };
    } catch (e: unknown) {
      return { success: false, error: e instanceof Error ? e.message : "Failed to fetch PLG Detail." };
    }
  },

  getPLGByName: async (plgName: string): Promise<ApiResponse<PLGDetail>> => {
    try {
      const docId = plgName.replace(/\s+/g, "_");
      const s = await getDoc(doc(db, "plgDetails", docId));
      if (!s.exists()) return { success: false, error: "PLG not found." };

      const rawData = s.data() as Record<string, unknown>;
      return { success: true, data: mapRawToDetail(rawData, plgName) };
    } catch (e: unknown) {
      return { success: false, error: e instanceof Error ? e.message : "Failed to fetch PLG." };
    }
  },

  searchLearners: async (searchQuery: string): Promise<ApiResponse<Learner[]>> => {
    if (!searchQuery || searchQuery.length < 3) return { success: true, data: [] };
    try {
      // users doc ID is the lowercase email — direct lookup, no where() needed
      const s = await getDoc(doc(db, "users", searchQuery.toLowerCase().trim()));
      if (!s.exists()) return { success: true, data: [] };
      const userData = s.data() as LearnerRow & { firstName: string; track: Track; plg: string };
      const learner: Learner = { ...userData, communityMemberId: s.id };
      return { success: true, data: [learner] };
    } catch (e: unknown) {
      return { success: false, error: e instanceof Error ? e.message : "Search failed" };
    }
  },

  getLearners: async (_plgId: string | number): Promise<ApiResponse<LearnerRow[]>> => {
    try {
      const q = query(collection(db, "users"), where("plgId", "==", Number(_plgId)));
      const s = await getDocs(q);
      const data: LearnerRow[] = s.docs.map(d => d.data() as LearnerRow);
      return { success: true, data };
    } catch (e: unknown) {
      return { success: false, error: e instanceof Error ? e.message : "Failed to fetch learners" };
    }
  },

  getActivationTrend: async (): Promise<ApiResponse<ActivationTrendData>> => {
    // export_to_firestore.py writes daily data only; weekly/monthly arrays are not populated
    try {
      const s = await getDoc(doc(db, "aggregates", "activationTrend"));
      if (s.exists()) return { success: true, data: s.data() as ActivationTrendData };
      return { success: false, error: "Trend data not found" };
    } catch {
      return { success: false, error: "Failed to fetch trend data" };
    }
  },

  getTrackPipeline: async (): Promise<ApiResponse<{ kcna: TrackPipeline; ckad: TrackPipeline }>> => {
    try {
      const [kcna, ckad] = await Promise.all([
        getDoc(doc(db, "aggregates", "pipeline_kcna")),
        getDoc(doc(db, "aggregates", "pipeline_ckad")),
      ]);
      if (kcna.exists() && ckad.exists()) {
        return { success: true, data: { kcna: kcna.data() as TrackPipeline, ckad: ckad.data() as TrackPipeline } };
      }
      return { success: false, error: "Pipeline data not found" };
    } catch {
      return { success: false, error: "Failed to fetch pipeline data" };
    }
  },

  getMentorWorkload: async (): Promise<ApiResponse<MentorWorkload[]>> => {
    try {
      const s = await getDoc(doc(db, "aggregates", "mentorWorkload"));
      if (s.exists()) {
        const d = s.data() as { workload: MentorWorkload[] };
        return { success: true, data: d.workload };
      }
      return { success: false, error: "Workload data not found" };
    } catch (e: unknown) {
      return { success: false, error: e instanceof Error ? e.message : "Failed to fetch mentor workload." };
    }
  },

  getAtRiskPLGs: async (_track?: string): Promise<ApiResponse<AtRiskPLG[]>> => {
     try {
      const s = await getDoc(doc(db, "aggregates", "atRisk"));
      if (s.exists()) {
        const d = s.data() as { plgs: AtRiskPLG[] };
        return { success: true, data: d.plgs };
      }
      return { success: false, error: "At-risk PLG data not found." };
    } catch {
      return { success: false, error: "Failed to fetch at-risk data." };
    }
  },

  getOperationalAlerts: async (): Promise<ApiResponse<OperationalAlert[]>> => {
    try {
      const s = await getDoc(doc(db, "aggregates", "alerts"));
      if (s.exists()) {
        const d = s.data() as { alerts: OperationalAlert[] };
        return { success: true, data: d.alerts };
      }
      return { success: false, error: "Alerts data not found" };
    } catch {
      return { success: false, error: "Failed to fetch alerts" };
    }
  },

  getDataQualityStats: async (): Promise<ApiResponse<DataQualityStats>> => {
    try {
      const s = await getDoc(doc(db, "dataQuality", "stats"));
      if (s.exists()) return { success: true, data: s.data() as DataQualityStats };
      return { success: false, error: "Data quality stats not found" };
    } catch {
      return { success: false, error: "Failed to fetch data quality stats" };
    }
  },

  sendDMs: (_uids: string[], _message: string): Promise<ApiResponse<void>> => {
    return Promise.resolve({ success: false, error: "Not yet implemented" });
  },

  updateMentorAssignment: (_plgId: string | number, _mentorEmail: string): Promise<ApiResponse<void>> => {
    return Promise.resolve({ success: false, error: "Not yet implemented" });
  },

  getMeetingSchedules: async (_plgId: string | number): Promise<ApiResponse<Meeting[]>> => {
    try {
      const s = await getDoc(doc(db, "aggregates", "meetingSchedules"));
      if (s.exists()) return { success: true, data: (s.data() as { meetings: Meeting[] }).meetings };
      return { success: false, error: "No meeting schedules found." };
    } catch (e: unknown) {
      return { success: false, error: e instanceof Error ? e.message : "Failed to fetch meeting schedules." };
    }
  },

  sendMeetingInvite: (_plgId: string | number, _meetingId: number): Promise<ApiResponse<void>> => {
    return Promise.resolve({ success: false, error: "Not yet implemented" });
  },

  updatePLGMetadata: (_plgId: string | number, _metadata: { tags?: string[]; subgroups?: string[] }): Promise<ApiResponse<void>> => {
    return Promise.resolve({ success: false, error: "Not yet implemented" });
  },

  getConversationStarters: async (): Promise<ApiResponse<ConversationStarter[]>> => {
    try {
      const s = await getDoc(doc(db, "aggregates", "conversationStarters"));
      if (s.exists()) return { success: true, data: (s.data() as { starters: ConversationStarter[] }).starters };
      return { success: false, error: "No conversation starters found." };
    } catch (e: unknown) {
      return { success: false, error: e instanceof Error ? e.message : "Failed to fetch conversation starters." };
    }
  },

  importCourses: async (rows: ImportRow[]): Promise<ApiResponse<ImportResult>> => {
    try {
      const fn = httpsCallable<{ rows: ImportRow[] }, ImportResult>(functions, "importCourses");
      const res = await fn({ rows });
      return { success: true, data: res.data };
    } catch (e: unknown) {
      return { success: false, error: e instanceof Error ? e.message : "Import failed" };
    }
  },

  submitFeedback: async (payload: {
    type: FeedbackType;
    description: string;
    page: string;
    email: string;
    role: string;
    requestedPlg?: string;
  }): Promise<ApiResponse<void>> => {
    try {
      await addDoc(collection(db, "feedback"), {
        ...payload,
        status: "open" satisfies FeedbackStatus,
        createdAt: serverTimestamp(),
      });
      return { success: true };
    } catch (e: unknown) {
      return { success: false, error: e instanceof Error ? e.message : "Failed to submit feedback" };
    }
  },

  // Public, unauthenticated write — see firestore.rules `progressReports` for
  // the server-enforced validation (bounded lengths, enum values, Credly URL shape).
  submitProgressReport: async (payload: ProgressReportPayload): Promise<ApiResponse<void>> => {
    try {
      await addDoc(collection(db, "progressReports"), {
        ...payload,
        submittedAt: serverTimestamp(),
      });
      return { success: true };
    } catch (e: unknown) {
      return { success: false, error: e instanceof Error ? e.message : "Failed to submit progress report" };
    }
  },

  getMyFeedback: async (email: string): Promise<ApiResponse<FeedbackItem[]>> => {
    try {
      const snap = await getDocs(
        query(collection(db, "feedback"), where("email", "==", email), orderBy("createdAt", "desc"))
      );
      const data: FeedbackItem[] = snap.docs.map(d => {
        const r = d.data() as Record<string, unknown>;
        return {
          id:           d.id,
          type:         r.type as FeedbackType,
          description:  r.description as string,
          page:         r.page as string,
          email:        r.email as string,
          role:         r.role as string,
          status:       (r.status as FeedbackStatus) ?? "open",
          createdAt:    r.createdAt ? (r.createdAt as { toDate: () => Date }).toDate().toISOString() : "",
          resolvedNote: r.resolvedNote as string | undefined,
          requestedPlg: r.requestedPlg as string | undefined,
        };
      });
      return { success: true, data };
    } catch (e: unknown) {
      return { success: false, error: e instanceof Error ? e.message : "Failed to fetch your feedback" };
    }
  },

  getFeedback: async (): Promise<ApiResponse<FeedbackItem[]>> => {
    try {
      const snap = await getDocs(
        query(collection(db, "feedback"), orderBy("createdAt", "desc"))
      );
      const data: FeedbackItem[] = snap.docs.map(d => {
        const r = d.data() as Record<string, unknown>;
        return {
          id:           d.id,
          type:         r.type as FeedbackType,
          description:  r.description as string,
          page:         r.page as string,
          email:        r.email as string,
          role:         r.role as string,
          status:       (r.status as FeedbackStatus) ?? "open",
          createdAt:    r.createdAt ? (r.createdAt as { toDate: () => Date }).toDate().toISOString() : "",
          resolvedNote: r.resolvedNote as string | undefined,
          requestedPlg: r.requestedPlg as string | undefined,
        };
      });
      return { success: true, data };
    } catch (e: unknown) {
      return { success: false, error: e instanceof Error ? e.message : "Failed to fetch feedback" };
    }
  },

  updateFeedbackStatus: async (
    id: string,
    status: FeedbackStatus,
    resolvedNote?: string,
  ): Promise<ApiResponse<void>> => {
    try {
      await updateDoc(doc(db, "feedback", id), {
        status,
        ...(resolvedNote ? { resolvedNote } : {}),
      });
      return { success: true };
    } catch (e: unknown) {
      return { success: false, error: e instanceof Error ? e.message : "Failed to update status" };
    }
  },

  approveMentorPlgRequest: async (
    feedbackId: string,
    plg: string,
    mode: "add" | "replace",
  ): Promise<ApiResponse<{ plg: string; mode: string; pendingFirstSignIn: boolean }>> => {
    try {
      const fn = httpsCallable<
        { feedbackId: string; plg: string; mode: string },
        { plg: string; mode: string; pendingFirstSignIn: boolean }
      >(functions, "approveMentorPlgRequest");
      const res = await fn({ feedbackId, plg, mode });
      return { success: true, data: res.data };
    } catch (e: unknown) {
      return { success: false, error: e instanceof Error ? e.message : "Failed to approve request" };
    }
  },

  getScheduledPosts: async (status?: ScheduledPostStatus): Promise<ApiResponse<ScheduledPost[]>> => {
    try {
      const user = auth.currentUser;
      if (!user) return { success: false, error: "Not authenticated", code: "NOT_AUTHENTICATED" };
      const qs = status ? `?status=${status}` : "";
      const fetchWith = (forceRefresh: boolean) =>
        user.getIdToken(forceRefresh).then(token =>
          fetch(`${SUPABASE_API_BASE}/posts/scheduled${qs}`, { headers: { Authorization: `Bearer ${token}` } })
        );

      let res = await fetchWith(false);
      if (res.status === 403) {
        // Cached ID token may carry stale role claims (e.g. just promoted to
        // admin) -- force one fresh token and retry before accepting the 403.
        res = await fetchWith(true);
      }

      const json = await res.json() as { success: boolean; data?: Record<string, unknown>[]; error?: string; code?: string };
      if (!json.success || !json.data) {
        return { success: false, error: json.error ?? "Failed to fetch scheduled posts", code: json.code };
      }
      const data: ScheduledPost[] = json.data.map(p => ({
        id:                p.id as number,
        title:             p.title as string,
        spaceIds:          (p.space_ids as number[]) ?? [],
        publishAt:         p.publish_at as string,
        status:            p.status as ScheduledPostStatus,
        publishedPostIds:  (p.published_post_ids as number[] | null) ?? null,
        error:             (p.error as string | null) ?? null,
        createdBy:         p.created_by as string,
        createdAt:         p.created_at as string,
        publishedAt:       (p.published_at as string | null) ?? null,
      }));
      return { success: true, data };
    } catch (e: unknown) {
      return { success: false, error: e instanceof Error ? e.message : "Failed to fetch scheduled posts" };
    }
  },

  listTeamMembers: async (): Promise<ApiResponse<TeamMember[]>> => {
    try {
      const q = query(collection(db, "users"), where("role", "in", ["admin", "program_manager", "mentor"]));
      const snap = await getDocs(q);
      const data: TeamMember[] = snap.docs.map((d) => {
        const raw = d.data() as Record<string, unknown>;
        return {
          email: d.id,
          name: (raw.name as string) ?? d.id.split("@")[0],
          role: (raw.role as UserRoleValue) ?? "learner",
          plg: (raw.plg as string) ?? undefined,
        };
      }).sort((a, b) => a.email.localeCompare(b.email));
      return { success: true, data };
    } catch (e: unknown) {
      return { success: false, error: e instanceof Error ? e.message : "Failed to fetch team members" };
    }
  },

  updateUserRole: async (
    email: string,
    role: UserRoleValue,
    plg?: string,
  ): Promise<ApiResponse<{ email: string; role: UserRoleValue; pendingFirstSignIn: boolean; circleRetagWarning?: string }>> => {
    try {
      const fn = httpsCallable<
        { email: string; role: UserRoleValue; plg?: string },
        { email: string; role: UserRoleValue; pendingFirstSignIn: boolean; circleRetagWarning?: string }
      >(functions, "updateUserRole");
      const res = await fn({ email, role, ...(plg ? { plg } : {}) });
      return { success: true, data: res.data };
    } catch (e: unknown) {
      return { success: false, error: e instanceof Error ? e.message : "Failed to update role" };
    }
  },

  // -- Calendar/booking (Supabase-backed, Firebase ID token auth -- see
  // supabase/functions/calendar-*, mentor-availability, list-open-slots,
  // create-booking, cancel-booking, booking-by-token) --

  getCalendarStatus: async (): Promise<ApiResponse<CalendarConnection[]>> => {
    try {
      const user = auth.currentUser;
      if (!user) return { success: false, error: "Not authenticated", code: "NOT_AUTHENTICATED" };
      const token = await user.getIdToken();
      const res = await fetch(`${SUPABASE_FUNCTIONS_BASE}/calendar-status`, { headers: { Authorization: `Bearer ${token}` } });
      const json = (await res.json()) as ApiResponse<CalendarConnection[]>;
      if (!json.success) return { success: false, error: json.error };
      return { success: true, data: json.data };
    } catch (e: unknown) {
      return { success: false, error: e instanceof Error ? e.message : "Failed to fetch calendar status" };
    }
  },

  startCalendarConnect: async (ownerId: string): Promise<ApiResponse<{ authUrl: string }>> => {
    try {
      const user = auth.currentUser;
      if (!user) return { success: false, error: "Not authenticated", code: "NOT_AUTHENTICATED" };
      const token = await user.getIdToken();
      const res = await fetch(`${SUPABASE_FUNCTIONS_BASE}/calendar-oauth-start`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ ownerId }),
      });
      const json = (await res.json()) as ApiResponse<{ authUrl: string }>;
      if (!json.success) return { success: false, error: json.error };
      return { success: true, data: json.data };
    } catch (e: unknown) {
      return { success: false, error: e instanceof Error ? e.message : "Failed to start calendar connect" };
    }
  },

  disconnectCalendar: async (ownerId: string): Promise<ApiResponse<{ ownerId: string; connected: boolean }>> => {
    try {
      const user = auth.currentUser;
      if (!user) return { success: false, error: "Not authenticated", code: "NOT_AUTHENTICATED" };
      const token = await user.getIdToken();
      const res = await fetch(`${SUPABASE_FUNCTIONS_BASE}/calendar-disconnect`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ ownerId }),
      });
      const json = (await res.json()) as ApiResponse<{ ownerId: string; connected: boolean }>;
      if (!json.success) return { success: false, error: json.error };
      return { success: true, data: json.data };
    } catch (e: unknown) {
      return { success: false, error: e instanceof Error ? e.message : "Failed to disconnect calendar" };
    }
  },

  getCalendarList: async (ownerId: string): Promise<ApiResponse<CalendarListEntry[]>> => {
    try {
      const user = auth.currentUser;
      if (!user) return { success: false, error: "Not authenticated", code: "NOT_AUTHENTICATED" };
      const token = await user.getIdToken();
      const res = await fetch(`${SUPABASE_FUNCTIONS_BASE}/calendar-list?ownerId=${encodeURIComponent(ownerId)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = (await res.json()) as ApiResponse<CalendarListEntry[]>;
      if (!json.success) return { success: false, error: json.error };
      return { success: true, data: json.data };
    } catch (e: unknown) {
      return { success: false, error: e instanceof Error ? e.message : "Failed to fetch calendar list" };
    }
  },

  selectCalendar: async (ownerId: string, calendarId: string): Promise<ApiResponse<{ ownerId: string; calendarId: string; calendarName: string }>> => {
    try {
      const user = auth.currentUser;
      if (!user) return { success: false, error: "Not authenticated", code: "NOT_AUTHENTICATED" };
      const token = await user.getIdToken();
      const res = await fetch(`${SUPABASE_FUNCTIONS_BASE}/calendar-select`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ ownerId, calendarId }),
      });
      const json = (await res.json()) as ApiResponse<{ ownerId: string; calendarId: string; calendarName: string }>;
      if (!json.success) return { success: false, error: json.error };
      return { success: true, data: json.data };
    } catch (e: unknown) {
      return { success: false, error: e instanceof Error ? e.message : "Failed to select calendar" };
    }
  },

  getMentorAvailability: async (mentorEmail: string): Promise<ApiResponse<MentorAvailability>> => {
    try {
      const user = auth.currentUser;
      if (!user) return { success: false, error: "Not authenticated", code: "NOT_AUTHENTICATED" };
      const token = await user.getIdToken();
      const res = await fetch(`${SUPABASE_FUNCTIONS_BASE}/mentor-availability?mentorEmail=${encodeURIComponent(mentorEmail)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = (await res.json()) as ApiResponse<MentorAvailability>;
      if (!json.success) return { success: false, error: json.error };
      return { success: true, data: json.data };
    } catch (e: unknown) {
      return { success: false, error: e instanceof Error ? e.message : "Failed to fetch availability" };
    }
  },

  setMentorAvailability: async (
    mentorEmail: string,
    timezone: string,
    weeklySlots: WeeklyAvailabilitySlot[],
    slotLengthMinutes: number,
  ): Promise<ApiResponse<{ mentorEmail: string; saved: boolean }>> => {
    try {
      const user = auth.currentUser;
      if (!user) return { success: false, error: "Not authenticated", code: "NOT_AUTHENTICATED" };
      const token = await user.getIdToken();
      const res = await fetch(`${SUPABASE_FUNCTIONS_BASE}/mentor-availability`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ mentorEmail, timezone, weeklySlots, slotLengthMinutes }),
      });
      const json = (await res.json()) as ApiResponse<{ mentorEmail: string; saved: boolean }>;
      if (!json.success) return { success: false, error: json.error };
      return { success: true, data: json.data };
    } catch (e: unknown) {
      return { success: false, error: e instanceof Error ? e.message : "Failed to save availability" };
    }
  },

  listOpenSlots: async (mentorEmail: string): Promise<ApiResponse<BookingSlot[]>> => {
    try {
      const res = await fetch(`${SUPABASE_FUNCTIONS_BASE}/list-open-slots?mentorEmail=${encodeURIComponent(mentorEmail)}`);
      const json = (await res.json()) as ApiResponse<BookingSlot[]>;
      if (!json.success) return { success: false, error: json.error };
      return { success: true, data: json.data };
    } catch (e: unknown) {
      return { success: false, error: e instanceof Error ? e.message : "Failed to fetch open slots" };
    }
  },

  createBooking: async (params: {
    mentorEmail: string; plg?: string; learnerEmail: string; learnerName: string; slotStart: string;
  }): Promise<ApiResponse<{ bookingId: number; cancelToken: string }>> => {
    try {
      const res = await fetch(`${SUPABASE_FUNCTIONS_BASE}/create-booking`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(params),
      });
      const json = (await res.json()) as ApiResponse<{ bookingId: number; cancelToken: string }>;
      if (!json.success) return { success: false, error: json.error, code: json.code };
      return { success: true, data: json.data };
    } catch (e: unknown) {
      return { success: false, error: e instanceof Error ? e.message : "Failed to create booking" };
    }
  },

  getBookingByToken: async (cancelToken: string): Promise<ApiResponse<Booking>> => {
    try {
      const res = await fetch(`${SUPABASE_FUNCTIONS_BASE}/booking-by-token?token=${encodeURIComponent(cancelToken)}`);
      const json = (await res.json()) as ApiResponse<Booking>;
      if (!json.success) return { success: false, error: json.error };
      return { success: true, data: json.data };
    } catch (e: unknown) {
      return { success: false, error: e instanceof Error ? e.message : "Failed to fetch booking" };
    }
  },

  cancelBooking: async (cancelToken: string): Promise<ApiResponse<{ cancelled: boolean }>> => {
    try {
      const res = await fetch(`${SUPABASE_FUNCTIONS_BASE}/cancel-booking`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cancelToken }),
      });
      const json = (await res.json()) as ApiResponse<{ cancelled: boolean }>;
      if (!json.success) return { success: false, error: json.error };
      return { success: true, data: json.data };
    } catch (e: unknown) {
      return { success: false, error: e instanceof Error ? e.message : "Failed to cancel booking" };
    }
  },

  getPlgCalendarChoice: async (plg: string): Promise<ApiResponse<{ plg: string; calendarOwnerId: string | null }>> => {
    try {
      const user = auth.currentUser;
      if (!user) return { success: false, error: "Not authenticated", code: "NOT_AUTHENTICATED" };
      const token = await user.getIdToken();
      const res = await fetch(`${SUPABASE_FUNCTIONS_BASE}/plg-calendar-choice?plg=${encodeURIComponent(plg)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = (await res.json()) as ApiResponse<{ plg: string; calendarOwnerId: string | null }>;
      if (!json.success) return { success: false, error: json.error };
      return { success: true, data: json.data };
    } catch (e: unknown) {
      return { success: false, error: e instanceof Error ? e.message : "Failed to fetch PLG calendar choice" };
    }
  },

  setPlgCalendarChoice: async (plg: string, calendarOwnerId: string | null): Promise<ApiResponse<{ plg: string; saved: boolean }>> => {
    try {
      const user = auth.currentUser;
      if (!user) return { success: false, error: "Not authenticated", code: "NOT_AUTHENTICATED" };
      const token = await user.getIdToken();
      const res = await fetch(`${SUPABASE_FUNCTIONS_BASE}/plg-calendar-choice`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ plg, calendarOwnerId }),
      });
      const json = (await res.json()) as ApiResponse<{ plg: string; saved: boolean }>;
      if (!json.success) return { success: false, error: json.error };
      return { success: true, data: json.data };
    } catch (e: unknown) {
      return { success: false, error: e instanceof Error ? e.message : "Failed to save PLG calendar choice" };
    }
  },

  createMessagingRequest: async (params: {
    plg: string; message: string; dueDate?: string; audienceSegment: AudienceSegment;
  }): Promise<ApiResponse<{ id: number; status: string; created_at: string }>> => {
    try {
      const user = auth.currentUser;
      if (!user) return { success: false, error: "Not authenticated", code: "NOT_AUTHENTICATED" };
      const token = await user.getIdToken();
      const res = await fetch(`${SUPABASE_FUNCTIONS_BASE}/messaging-request-create`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(params),
      });
      const json = (await res.json()) as ApiResponse<{ id: number; status: string; created_at: string }>;
      if (!json.success) return { success: false, error: json.error };
      return { success: true, data: json.data };
    } catch (e: unknown) {
      return { success: false, error: e instanceof Error ? e.message : "Failed to create messaging request" };
    }
  },

  getMessagingRequests: async (filters?: { plg?: string; status?: string }): Promise<ApiResponse<MessagingRequest[]>> => {
    try {
      const user = auth.currentUser;
      if (!user) return { success: false, error: "Not authenticated", code: "NOT_AUTHENTICATED" };
      const token = await user.getIdToken();
      const qs = new URLSearchParams();
      if (filters?.plg) qs.set("plg", filters.plg);
      if (filters?.status) qs.set("status", filters.status);
      const suffix = qs.toString() ? `?${qs.toString()}` : "";
      const res = await fetch(`${SUPABASE_FUNCTIONS_BASE}/messaging-request-list${suffix}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = (await res.json()) as ApiResponse<MessagingRequest[]>;
      if (!json.success) return { success: false, error: json.error };
      return { success: true, data: json.data };
    } catch (e: unknown) {
      return { success: false, error: e instanceof Error ? e.message : "Failed to fetch messaging requests" };
    }
  },

  updateMessagingRequestStatus: async (
    requestId: number, status: "approved" | "rejected" | "sent", reviewNote?: string,
  ): Promise<ApiResponse<{ id: number; status: string }>> => {
    try {
      const user = auth.currentUser;
      if (!user) return { success: false, error: "Not authenticated", code: "NOT_AUTHENTICATED" };
      const token = await user.getIdToken();
      const res = await fetch(`${SUPABASE_FUNCTIONS_BASE}/messaging-request-update-status`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ requestId, status, reviewNote }),
      });
      const json = (await res.json()) as ApiResponse<{ id: number; status: string }>;
      if (!json.success) return { success: false, error: json.error };
      return { success: true, data: json.data };
    } catch (e: unknown) {
      return { success: false, error: e instanceof Error ? e.message : "Failed to update messaging request" };
    }
  },
};
